import { useToastQueueContext } from '#root/contexts/ToastContext';

/**
 * Hook principal para notificaciones con Toast V2
 * Utiliza el nuevo sistema de queue con prioridad y animaciones
 *
 * Features:
 * - Cola con máximo 4 toasts simultáneos
 * - Priorización automática (error > warning > success > info)
 * - Duración dinámica basada en longitud del mensaje
 * - Animaciones suaves y diseño moderno
 * - Responsive: bottom-right en desktop, top-center en móvil
 * - Toast Keys: reemplazar toasts previos con mismo key
 */
const useNotification = () => {
  const queue = useToastQueueContext();

  return {
    createMessage: {
      error: (text: string, key?: string) => queue.addToast(text, 'error', { key }),
      info: (text: string, key?: string) => queue.addToast(text, 'info', { key }),
      success: (text: string, key?: string) => queue.addToast(text, 'success', { key }),
      warning: (text: string, key?: string) => queue.addToast(text, 'warning', { key }),
      /**
       * Reemplazar toast existente con el mismo key
       * @param key - Key del toast a reemplazar
       * @param text - Nuevo mensaje
       * @param variant - Variante del toast
       */
      replace: (key: string, text: string, variant: 'error' | 'info' | 'success' | 'warning') =>
        queue.replaceToast(key, text, variant),
    },
    closeMessage: queue.clearAll,
    // Legacy compatibility para componentes que aún usan estos campos
    notification: {
      message: '',
      state: false,
      variant: 'info' as const,
    },
    // Acceso al queue completo si es necesario
    queue,
  };
};

export default useNotification;

// Export alternativo para compatibilidad durante migración
export const useNotificationWithQueue = useNotification;
